// @flow
import * as React from 'react';
import styled, {keyframes} from 'styled-components';
import {LayoutWrapper, useLayoutState} from 'components/Layout';
import {LocalizedParagraph, Paragraph, useLocalized} from 'components/Localized';
import Image, {BackgroundImage} from 'components/Image';
import Space from 'components/Space';

type Props = {
  show: boolean,
  title: Array<string>,
  step: Array<string>,
  stepTitle: Array<string>,
  images: Array<string>,
  onStep: Array<() => void>,
}

export default function GuideStep(props: Props): React.Element<any> {
  const layoutState = useLayoutState();
  //HACK (256/711) / (props.theme.layout.height / props.theme.layout.width) <- FIXME
  const topMargin = React.useMemo(() => {
    return - Math.floor(30 * Math.min((256/711) / (layoutState.height / layoutState.width), 1.0));
  }, [layoutState])
  return (
    <>
    <GuideBackWrapper id="GuideBackWrapper" dataheaderheightratio={0.46} datatopmargin={topMargin}>
      <BackgroundImage3 source={props.images[2]}>
        <GuideStepMessage3 onClick={props.onStep[2]}>
          <GuideStepBack source="./driving-zone/img-guide-step.png">
            <Paragraph textKey={props.step[2]} />
          </GuideStepBack>
          <div>
            <Paragraph textKey={props.stepTitle[2]} />
          </div>
        </GuideStepMessage3>
      </BackgroundImage3>
      <BackgroundImage2 source={props.images[1]}>
        <GuideStepMessage2 onClick={props.onStep[1]}>
          <GuideStepBack source="./driving-zone/img-guide-step.png">
            <Paragraph textKey={props.step[1]} />
          </GuideStepBack>
          <div>
            <Paragraph textKey={props.stepTitle[1]} />
          </div>
        </GuideStepMessage2>
      </BackgroundImage2>
      <BackgroundImage1 source={props.images[0]}>
        <GuideStepMessage1 onClick={props.onStep[0]}>
          <GuideStepBack source="./driving-zone/img-guide-step.png">
            <Paragraph textKey={props.step[0]} />
          </GuideStepBack>
          <div>
            <Paragraph textKey={props.stepTitle[0]} />
          </div>
        </GuideStepMessage1>
      </BackgroundImage1>
    </GuideBackWrapper>
    <GuideStepWrapper dataheaderheightratio={0.44} datatopmargin={topMargin}>
      <Paragraph textKey={props.title[0]} />
      <Space heightM={6 * (layoutState.scale ?? 1)} heightD={13 * (layoutState.scale ?? 1)} />
      <GuideStepTitle textKey={props.title[1]} />
    </GuideStepWrapper>
    </>
  );
}

type PropsTitle = {
  textKey: string,
}

function GuideStepTitle(props: PropsTitle): React.Element<any> {
  const localized = useLocalized();
  const localizedText = React.useMemo(() => {
    return localized[props.textKey];
  }, [localized, props.textKey]);
  const elements = React.useMemo(() => {
    const texts = localizedText.datatext.split(/(ⓑ)/);
    return texts.map((value, index) => {
      if (value === 'ⓑ') {
        return <ButtonImage key={index} source="./driving-zone/btn-top-index.png" />
      }
      return <span key={index}>{value}</span>;
    });
  }, [localizedText]);

  return (
    <LocalizedParagraph {...localizedText} datatext="">
      {elements}
    </LocalizedParagraph>
  );
}

const GuideStepWrapper = styled.div`
  width: 100%;
  height: calc(${props => props.theme.layout.height * props.dataheaderheightratio}px - ${props => props.theme.layout.mobile ? 41 * (Math.max(props.theme.layout.scale ?? 1, 1)) : 97}px + ${props => props.datatopmargin}px);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding-top: ${props => props.theme.layout.mobile ? 41 * (Math.max(props.theme.layout.scale ?? 1, 1)) : 97}px;
`;

const GuideStepBack = styled(BackgroundImage)`
  width: ${props => props.theme.layout.mobile ? 87 : 160}px;
  height: ${props => props.theme.layout.mobile ? 27 : 50}px;
  margin-bottom: ${props => props.theme.layout.mobile ? 21 * (props.theme.layout.scale ?? 1) : 45 * (props.theme.layout.scale ?? 1)}px;

  background-size: cover;
  background-repeat: no-repeat;
  pointer-events: auto;

  display: flex;
  align-items: center;
  justify-content: center;
`;


const leftToRight = keyframes`
  0% {
    transform: translateX(-250%);
  }
  100% {
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;


const GuideStepMessage = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;

  > :nth-child(1) {
    position: relative;
  }

  > :nth-child(2) {
    position: relative;
    padding: 0px ${props => props.theme.layout.mobile ? 16 : 28}px;
    word-break: keep-all;
  }
`;

const BackgroundImage1 = styled(BackgroundImage)`
  animation: ${fadeIn} 0.25s;
  animation-fill-mode: both;
  animation-delay: 0s;
  background-position: left center;
`;

const BackgroundImage2 = styled(BackgroundImage)`
  animation: ${fadeIn} 0.25s;
  animation-fill-mode: both;
  animation-delay: 0.25s;
  background-position: center center;
`;
const BackgroundImage3 = styled(BackgroundImage)`
  animation: ${fadeIn} 0.25s;
  animation-fill-mode: both;
  animation-delay: 0.5s;
  background-position: 50% 50%;

`;

const GuideStepMessage1 = styled(GuideStepMessage)`
  animation: ${leftToRight} 1.0s;
  animation-delay: 0s;
  animation-fill-mode: both;
`;
const GuideStepMessage2 = styled(GuideStepMessage)`
  animation: ${leftToRight} 1.0s;
  animation-delay: 0.5s;
  animation-fill-mode: both;
`;
const GuideStepMessage3 = styled(GuideStepMessage)`
  animation: ${leftToRight} 1.0s;
  animation-delay: 1s;
  animation-fill-mode: both;
`;

const GuideBackWrapper = styled(LayoutWrapper)`
  display: flex;
  flex-direction: row-reverse;
  pointer-events: auto;

  > div {
    display: flex;
    justify-content: center;
    position: absolute;
    left: 0px;
    top: 0px;
    background-size: cover;
    
    background-repeat: no-repeat;
    width: 100%;
    height: calc(100% - ${props => props.theme.layout.height * props.dataheaderheightratio}px);
    padding-top: ${props => props.theme.layout.height * props.dataheaderheightratio}px;
    button {
      position: relative;
      top: ${props => props.datatopmargin}px;
    }
  }

  > :nth-child(1) {
    width: calc(34% - env(safe-area-inset-right));
    padding-left: 66%;
    padding-right: calc(env(safe-area-inset-right));
    clip-path: polygon(73% 0, 100% 0, 100% 100%, 60% 100%);

  }
  > :nth-child(2) {
    width: 34%;
    padding-left: 32%;
    padding-right: 34%;
    clip-path: polygon(39% 0, 73% 0, 60% 100%, 25% 100%);

  }
  > :nth-child(3) {
    width: calc(32% - env(safe-area-inset-left));
    padding-left: calc(env(safe-area-inset-left));
    padding-right: 68%;
    clip-path: polygon(0 0, 39% 0, 26% 100%, 0 100%);
  }
`;

const ButtonImage = styled(Image)`
  width: ${props => props.theme.layout.mobile ? 24 : 48}px;
  height: ${props => props.theme.layout.mobile ? 24 : 48}px;
  margin-bottom: ${props => props.theme.layout.mobile ? 5 : 2}px;
  vertical-align: middle;
`;